"use client";

import { useRefresh } from "@/app/context/RefreshContext";
import type { StepProgress } from "@/app/context/RefreshContext";

function StepIcon({ status }: { status: StepProgress["status"] }) {
  if (status === "done")    return <span className="text-green-600 font-bold">✓</span>;
  if (status === "warn")    return <span className="text-yellow-500 font-bold">⚠</span>;
  if (status === "error")   return <span className="text-red-500 font-bold">✗</span>;
  if (status === "loading") return <span className="text-blue-500 animate-spin inline-block">⟳</span>;
  return <span className="text-gray-300">○</span>;
}

function stepLabelColor(status: StepProgress["status"]) {
  if (status === "done")    return "text-green-700";
  if (status === "warn")    return "text-yellow-700";
  if (status === "error")   return "text-red-600 font-medium";
  if (status === "loading") return "text-blue-700 font-medium";
  return "text-gray-400";
}

function formatDuration(ms: number | null) {
  if (!ms) return "";
  if (ms < 60000) return `${Math.round(ms / 1000)}с`;
  return `${Math.floor(ms / 60000)}м ${Math.round((ms % 60000) / 1000)}с`;
}

export default function RefreshModal() {
  const { modalOpen, closeModal, steps, status, durationMs, targetStoreId, startRefresh, retryStep, copyLog } = useRefresh();

  if (!modalOpen) return null;

  const isRunning  = status === "running";
  const isAuthError = status === "auth_error";
  const isDone     = status === "done" || status === "warn" || status === "error" || isAuthError;
  const hasErrors  = steps.some(s => s.status === "error");
  const hasWarns   = steps.some(s => s.status === "warn");

  const doneCount  = steps.filter(s => s.status === "done").length;
  const warnCount  = steps.filter(s => s.status === "warn").length;
  const errorCount = steps.filter(s => s.status === "error").length;
  const dataSteps = steps.filter(step => step.phase !== "post");
  const postSteps = steps.filter(step => step.phase === "post");
  const isPostProcessing = isRunning &&
    dataSteps.every(step => step.status === "done" || step.status === "warn" || step.status === "error") &&
    postSteps.some(step => step.status === "pending" || step.status === "loading");

  const handleCopy = () => {
    const text = copyLog();
    navigator.clipboard.writeText(text).catch(() => {});
  };

  return (
    <>
      {/* Backdrop. Клик сворачивает модалку — пайплайн продолжает работать в фоне,
          статус видно в топ-баре ("Обновляется..."), клик по нему откроет снова. */}
      <div
        className="fixed inset-0 z-40 bg-black/20 ui-modal-backdrop"
        onClick={closeModal}
      />

      {/* Modal */}
      <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden ui-modal-panel">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              {isRunning && (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              )}
              {isDone && !hasErrors && !hasWarns && !isAuthError && <span className="text-green-600 text-lg">✓</span>}
              {isDone && hasWarns && !hasErrors && !isAuthError && <span className="text-yellow-500 text-lg">⚠</span>}
              {isDone && hasErrors && !isAuthError && <span className="text-red-500 text-lg">✗</span>}
              {isAuthError && <span className="text-red-500 text-lg">🔒</span>}
              <span className="font-semibold text-gray-800 text-sm">
                {isRunning
                  ? isPostProcessing ? "Проверяем действия после обновления..." : "Обновление данных..."
                  : isAuthError ? "Ошибка подключения Ozon" : status === "error" ? "Обновление не удалось" : status === "warn" || hasWarns ? "Обновление с предупреждениями" : hasErrors ? "Обновление с ошибками" : "Обновление завершено"}
              </span>
              {isDone && durationMs && (
                <span className="text-xs text-gray-400">{formatDuration(durationMs)}</span>
              )}
            </div>
            <button
              onClick={closeModal}
              title={isRunning ? "Свернуть (обновление продолжится в фоне)" : "Закрыть"}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none w-6 h-6 flex items-center justify-center"
            >
              {isRunning ? "—" : "×"}
            </button>
          </div>

          {/* Auth error banner */}
          {isAuthError && (
            <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <div className="font-medium mb-1">Не удалось обновить данные: проверьте подключение магазина</div>
              <div className="text-xs text-red-700 leading-relaxed">
                {steps.find(step => step.status === "error")?.error || "Ozon не принял ключи подключения."}{" "}
                Откройте{" "}
                <a href="/settings" className="underline font-medium">Настройки → API ключи</a>{" "}
                и проверьте Client ID и API Key этого магазина. Сохранённые данные не удалены; после исправления подключения запустите обновление снова.
              </div>
            </div>
          )}

          {/* Steps list */}
          <div className="px-5 py-4 max-h-96 overflow-y-auto">
            {steps.length === 0 && !isAuthError && (
              <p className="text-sm text-gray-400 text-center py-4">Подключаемся...</p>
            )}
            <div className="flex flex-col gap-2">
              {dataSteps.map(step => (
                <div key={step.stepId} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 w-4 shrink-0 text-center">
                    <StepIcon status={step.status} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={stepLabelColor(step.status)}>
                        {step.label}
                      </span>
                      {step.count != null && (
                        <span className="text-xs text-gray-400">({step.count})</span>
                      )}
                      {step.status === "error" && (
                        <button
                          onClick={() => retryStep(step.stepId)}
                          className="text-xs text-blue-500 hover:text-blue-700 ml-auto shrink-0"
                        >
                          ↻ Повторить
                        </button>
                      )}
                    </div>
                    {step.error && (
                      <div className={`mt-0.5 text-xs px-2 py-1 rounded break-all ${
                        step.status === "warn"
                          ? "text-yellow-700 bg-yellow-50"
                          : "text-red-600 bg-red-50 font-mono"
                      }`}>
                        {step.error}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {postSteps.length > 0 && (
                <div className="pt-2 mt-1 border-t border-gray-100">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">После обновления данных</p>
                  <div className="flex flex-col gap-2">
                    {postSteps.map(step => (
                      <div key={step.stepId} className="flex items-start gap-2.5 text-sm">
                        <span className="mt-0.5 w-4 shrink-0 text-center">
                          <StepIcon status={step.status} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={stepLabelColor(step.status)}>{step.label}</span>
                            {step.count != null && <span className="text-xs text-gray-400">({step.count})</span>}
                          </div>
                          {step.error && (
                            <div className="mt-0.5 text-xs px-2 py-1 rounded break-all text-yellow-700 bg-yellow-50">
                              {step.error}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          {isDone && (
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-2 bg-gray-50/50">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="text-green-600 font-medium">{doneCount} ✓</span>
                {warnCount > 0 && <span className="text-yellow-600 font-medium">{warnCount} ⚠</span>}
                {errorCount > 0 && <span className="text-red-600 font-medium">{errorCount} ✗</span>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                  title="Скопировать лог для отладки"
                >
                  Скопировать лог
                </button>
                {(hasErrors || hasWarns) && (
                  <button
                    onClick={() => startRefresh(undefined, targetStoreId)}
                    className="text-xs text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1 rounded-md transition-colors"
                  >
                    ↻ Повторить всё
                  </button>
                )}
                <button
                  onClick={closeModal}
                  className="text-xs text-gray-600 hover:text-gray-800 px-2.5 py-1 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Закрыть
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
